// Copyright (c) 2021. Sendanor <info@sendanor.fi>. All rights reserved.

import * as React from "react";

import { BrowserRouter as Router, NavLink, Route, Switch, useParams } from "react-router-dom";

import AppRoute from "../../constants/AppRoute";
import IFormView from "../views/public/iframe/IFormView";
import './FormApp.scss';
import FormView from "../views/form/item/FormView";
import HomeView from "../views/home/HomeView";
import FormAppClassName from "../../constants/FormAppClassName";
import { VoidCallback } from "../../nor/ts/interfaces/callbacks";
import SessionService, {
    SessionServiceDestructor,
    SessionServiceEvent
} from "../../services/SessionService";
import FormListView from "../views/form/list/FormListView";
import PublicFormView from "../views/public/form/PublicFormView";
import FormValueListView from "../views/form/value/list/FormValueListView";
import Icon from "../../nor/ui/components/icon/Icon";
import { AppLogo } from "../../assets/logo";
import LogService from "../../nor/ts/LogService";
import { HistoryServiceSwitch } from "../../nor/ui/components/router/HistoryServiceSwitch";
import { SignInIcon, SignOutIcon } from "../../assets/icon";
import Button from "../../nor/ui/components/button/Button";
import FormLoginModal from "../../modals/FormLoginModal";
import PipelineListView from "../views/pipeline/list/PipelineListView";
import PipelineView from "../views/pipeline/item/PipelineView";
import AgentListView from "../views/agent/list/AgentListView";
import AgentView from "../views/agent/item/AgentView";
import PipelineRunView from "../views/pipeline/run/item/PipelineRunView";
import PipelineRunListView from "../views/pipeline/run/list/PipelineRunListView";
import ModalGroupContainer from "../../nor/ui/components/modal/ModalGroupContainer";

const LOG = LogService.createLogger('FormApp');

export interface FormAppProps {
    className?: string;
}

export interface FormAppState {

    hasSession       : boolean;

}

export interface FormViewRouteParams {
    readonly id      : string
    readonly entryId : string
}

export interface PipelineViewRouteParams {
    readonly id    : string
    readonly runId : string
}

export interface AgentViewRouteParams {
    readonly id    : string
}

// export function FormValueViewWithParam (props: {hasSession: boolean}) : any {
//     const { hasSession } = props;
//     let { id, entryId } = useParams<FormViewRouteParams>();
//     return (
//         <FormValueView
//             id={id}
//             entryId={entryId}
//             hasSession={hasSession}
//         />
//     );
// }

export function FormValueListViewWithParam (props: {hasSession: boolean}) : any {
    const { hasSession } = props;
    let { id } = useParams<FormViewRouteParams>();
    const unescapedId = decodeURIComponent(id);
    LOG.debug(`FormValueListViewWithParam: id = `, id, unescapedId);
    return (
        <FormValueListView
            id={unescapedId}
            hasSession={hasSession}
        />
    );
}

export function FormViewWithParam (props: {hasSession: boolean}) : any {
    const { hasSession } = props;
    let { id } = useParams<PipelineViewRouteParams>();
    const unescapedId = decodeURIComponent(id);
    LOG.debug(`FormViewWithParam: id = `, id, unescapedId);
    return (
        <FormView
            id={unescapedId}
            hasSession={hasSession}
        />
    );
}

export function PipelineViewWithParam (props: {hasSession: boolean}) : any {
    const { hasSession } = props;
    let { id } = useParams<PipelineViewRouteParams>();
    const unescapedId = decodeURIComponent(id);
    LOG.debug(`PipelineViewWithParam: id = `, id, unescapedId);
    return (
        <PipelineView
            id={unescapedId}
            hasSession={hasSession}
        />
    );
}

export function PipelineRunViewWithParams (props: {hasSession: boolean}) : any {
    const { hasSession } = props;
    let { id, runId } = useParams<PipelineViewRouteParams>();
    const unescapedId = decodeURIComponent(id);
    const unescapedRunId = decodeURIComponent(runId);
    LOG.debug(`PipelineRunViewWithParam: id = `, id, unescapedId, runId, unescapedRunId);
    return (
        <PipelineRunView
            id={unescapedId}
            runId={unescapedRunId}
            hasSession={hasSession}
        />
    );
}

export function PipelineRunListViewWithParam (props: {hasSession: boolean}) : any {
    const { hasSession } = props;
    let { id } = useParams<PipelineViewRouteParams>();
    const unescapedId = decodeURIComponent(id);
    LOG.debug(`PipelineRunViewWithParam: id = `, id, unescapedId);
    return (
        <PipelineRunListView
            id={unescapedId}
            hasSession={hasSession}
        />
    );
}

export function AgentViewWithParam (props: {hasSession: boolean}) : any {
    const { hasSession } = props;
    let { id } = useParams<AgentViewRouteParams>();
    const unescapedId = decodeURIComponent(id);
    LOG.debug(`AgentViewWithParam: id = `, id, unescapedId);
    return (
        <AgentView
            id={unescapedId}
            hasSession={hasSession}
        />
    );
}

export function PublicFormViewWithParam (props: {}) : any {
    let { id } = useParams<AgentViewRouteParams>();
    const unescapedId = decodeURIComponent(id);
    LOG.debug(`PublicFormViewWithParam: id = `, id, unescapedId);
    return (
        <PublicFormView
            id={unescapedId}
        />
    );
}

export class FormApp extends React.Component<FormAppProps, FormAppState> {

    public static defaultProps: Partial<FormAppProps> = {};

    private readonly _sessionChangeCallback : VoidCallback;
    private readonly _loginCallback         : VoidCallback;
    private readonly _logoutCallback        : VoidCallback;

    private _sessionChangeListener : SessionServiceDestructor | undefined;

    public constructor (props: FormAppProps) {

        super(props);

        this.state = {
            hasSession: SessionService.hasSession()
        };

        this._sessionChangeCallback = this._onSessionChange.bind(this);
        this._loginCallback         = this._onLogin.bind(this);
        this._logoutCallback        = this._onLogout.bind(this);

    }

    public componentDidMount (): void {

        this._updateSessionChange();

        this._sessionChangeListener = SessionService.on(SessionServiceEvent.CHANGED, this._sessionChangeCallback);

    }

    public componentWillUnmount (): void {

        if (this._sessionChangeListener) {
            this._sessionChangeListener();
            this._sessionChangeListener = undefined;
        }

    }

    public render () {

        return (
            <Router>
                <HistoryServiceSwitch>

                    <Route exact path={AppRoute.I_FORM}>
                        <IFormView />
                    </Route>

                    <Route exact path={AppRoute.PUBLIC_FORM_VIEW}>
                        <PublicFormViewWithParam />
                    </Route>

                    <Route path={AppRoute.INDEX}>

                        <div className={FormAppClassName.FORM_APP}>

                            <header className={FormAppClassName.FORM_APP + '-header'}>

                                <div className={FormAppClassName.FORM_APP + '-title'}>
                                    <NavLink
                                        className={FormAppClassName.FORM_APP + '-title-link'}
                                        to={AppRoute.INDEX}
                                        exact
                                    >
                                        <Icon className={FormAppClassName.FORM_APP + '-logo'}><AppLogo /></Icon>
                                        <span className={FormAppClassName.FORM_APP + '-name'}>Lomake.app</span>
                                    </NavLink>
                                </div>

                                <nav className={FormAppClassName.FORM_APP + '-nav'}>
                                    <ul>

                                        <li><NavLink to={AppRoute.INDEX} exact activeClassName={FormAppClassName.NAV_LINK_ACTIVE}>Home</NavLink></li>

                                        {this.state.hasSession ? (
                                            <React.Fragment>

                                                <li><NavLink to={AppRoute.FORM_LIST_VIEW} activeClassName={FormAppClassName.NAV_LINK_ACTIVE}>Forms</NavLink></li>

                                                <li><NavLink to={AppRoute.PIPELINE_LIST_VIEW} activeClassName={FormAppClassName.NAV_LINK_ACTIVE}>Pipelines</NavLink></li>

                                                <li><NavLink to={AppRoute.AGENT_LIST_VIEW} activeClassName={FormAppClassName.NAV_LINK_ACTIVE}>Agents</NavLink></li>

                                            </React.Fragment>
                                        ) : null}

                                    </ul>
                                </nav>

                                <div className={FormAppClassName.FORM_APP + '-buttons'}>
                                    {this.state.hasSession ? (
                                        <Button click={this._logoutCallback}>
                                            <Icon><SignOutIcon /></Icon>
                                        </Button>
                                    ) : (
                                        <Button click={this._loginCallback}>
                                            <Icon><SignInIcon /></Icon>
                                        </Button>
                                    )}
                                </div>

                            </header>

                            <section className={FormAppClassName.FORM_APP + '-content'}>

                                <Switch>

                                    <Route exact path={AppRoute.FORM_VALUE_LIST_VIEW}>
                                        <FormValueListViewWithParam hasSession={this.state.hasSession} />
                                    </Route>

                                    <Route exact path={AppRoute.FORM_LIST_VIEW}>
                                        <FormListView hasSession={this.state.hasSession} />
                                    </Route>

                                    <Route exact path={AppRoute.PIPELINE_RUN_VIEW}>
                                        <PipelineRunViewWithParams hasSession={this.state.hasSession} />
                                    </Route>

                                    <Route exact path={AppRoute.PIPELINE_RUN_LIST_VIEW}>
                                        <PipelineRunListViewWithParam hasSession={this.state.hasSession} />
                                    </Route>

                                    <Route exact path={AppRoute.PIPELINE_VIEW}>
                                        <PipelineViewWithParam hasSession={this.state.hasSession} />
                                    </Route>

                                    <Route exact path={AppRoute.PIPELINE_LIST_VIEW}>
                                        <PipelineListView hasSession={this.state.hasSession} />
                                    </Route>

                                    <Route exact path={AppRoute.AGENT_VIEW}>
                                        <AgentViewWithParam hasSession={this.state.hasSession} />
                                    </Route>

                                    <Route exact path={AppRoute.AGENT_LIST_VIEW}>
                                        <AgentListView hasSession={this.state.hasSession} />
                                    </Route>

                                    <Route exact path={AppRoute.FORM_VIEW}>
                                        <FormViewWithParam hasSession={this.state.hasSession} />
                                    </Route>

                                    <Route exact path={AppRoute.INDEX}>
                                        <HomeView hasSession={this.state.hasSession} />
                                    </Route>

                                </Switch>

                            </section>

                        </div>

                    </Route>

                </HistoryServiceSwitch>

                <ModalGroupContainer />

            </Router>
        );

    }


    private _onSessionChange () {
        this._updateSessionChange();
    }

    private _onLogin () {
        LOG.debug('Opening modal for login...');
        FormLoginModal.openModal();
    }

    private _onLogout () {
        SessionService.logout();
    }

    private _updateSessionChange () {

        const hasSession = SessionService.hasSession();
        if ( this.state.hasSession !== hasSession ) {
            LOG.debug(`Session state changed: ${hasSession ? 'has session' : 'no session'}`);
            this.setState(() => ({hasSession: SessionService.hasSession()}));
        }

    }

}

export default FormApp;
